import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ClerkProvider, RedirectToSignIn, SignedIn, SignedOut, UserButton, useAuth } from '@clerk/clerk-react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';

import {
  ARRIVAL_SOURCE, collabKillSwitchShown, collabLabsClicked, collabPageViewed, type AnalyticsEvent,
} from '../core/events';
import {
  createAnalytics, nullAnalytics, stopAnalytics, type Analytics,
} from './analytics/client';
import { COLLAB_UNREACHABLE, call, type GetToken, type GroupSummary, type Me, type Saver } from './api';
import {
  chosenE2eIdentity, clerkRedirects, e2eIdentities, emailOfIdentity, publishableKey,
  selectE2eIdentity, signInAsE2eIdentity,
} from './auth-config';
import GroupList from './components/GroupList';
import DeclinePage from './components/DeclinePage';
import GroupRoom from './components/GroupRoom';
import JoinPage from './components/JoinPage';
import MaterialPage from './components/MaterialPage';
import MaterialUploadPage from './components/MaterialUploadPage';
import type { FetchState } from './components/MaterialList';
import NewGroupPage from './components/NewGroupPage';
import PendingInvitations from './components/PendingInvitations';
import PlannerBanner from './components/PlannerBanner';
import ShellHeader from './components/ShellHeader';
import { asStartedGroup, type StartedGroup } from './components/StartedGroupPanel';
import { clearDeclineToken, clearInviteToken, heldDeclineToken, heldInviteToken } from './invite-token';
import { browserHandoffStorage, plannerSession, type PlannerSession } from './planner-handoff';
import { UploadsProvider } from './uploads';
import type { PdfEngine } from './pdf';
import {
  groupPath, groupsPath, materialPath,
  ROUTE_DECLINE, ROUTE_FROM_PLANNER, ROUTE_GROUP, ROUTE_GROUPS, ROUTE_JOIN, ROUTE_MATERIAL, ROUTE_NEW_GROUP,
  ROUTE_NEW_MATERIAL,
} from './routes';

/**
 * The signed-in tool, and its routes.
 *
 * Access is decided by the Worker, not here. The page asks `/api/me` and renders what it is
 * told — it never re-derives the rule, because a rule stated in two places is a rule that
 * will disagree with itself. The one decision this side makes is cosmetic and permissive:
 * an unresolved kill switch leaves the tool up (R20, AE7).
 *
 * Five routes, and every one of them is a real address a cold load reaches:
 * `not_found_handling: "single-page-application"` in `wrangler.jsonc` hands the SPA shell to
 * any path the asset store does not recognise, which is what makes `/g/:groupId/m/:materialId`
 * openable from an email rather than only from a click inside the app.
 *
 * **No route ever carries a token.** Not the invitation token — it is captured out of the
 * fragment before any of this mounts and held in `sessionStorage` (`invite-token.ts`) — and
 * not the Clerk JWT, which travels in an `Authorization` header (`api.ts`). A held invitation
 * still renders the join screen *wherever* the teacher lands, because Clerk's hosted sign-in
 * returns them to `/` with nothing in the URL at all (`auth-config.ts`).
 */

/**
 * What a refused teacher reads. Keyed by the reason the Worker sent, which is a closed union
 * in `core/access.ts`; anything unrecognised falls through to `unreachable`.
 *
 * Every line has to be true and has to leave the reader knowing whether to do something.
 * A blank screen or a generic error leaves a refused teacher thinking they are broken.
 */
const DENIAL_MESSAGES: Record<string, string> = {
  disabled: 'Collab is switched off at the moment. Nothing you shared has gone anywhere.',
  no_email:
    'Your sign-in did not include an email address, and Collab needs one to show you to your '
    + 'group. Signing out and back in usually adds it; if it does not, tell us.',
  session_invalid: 'We could not verify your sign-in. Signing out and back in usually fixes it.',
  unreachable: COLLAB_UNREACHABLE,
};

export interface Auth {
  getToken: GetToken;
  userId: string | null;
}

export interface CollabAppProps {
  /** Supplied by whichever wrapper is in play — Clerk, or the browser-test identity. */
  auth: Auth;
  /**
   * Rendered in the header. Clerk's own components throw outside a `ClerkProvider`, so
   * `UserButton` is passed in by the wrapper that has one rather than imported here.
   */
  headerAction?: ReactNode;
  /** Injected in tests. In the browser the real adapter is built after sign-in. */
  analytics?: Analytics;
  /** The invitation token `invite-token.ts` captured, if this session is a join. */
  inviteToken?: string | null;
  /**
   * The token a `/decline` link carried, held under its own key (KTD7). Only `DeclinePage`
   * ever sees it, and it only ever posts it to the decline route.
   */
  declineToken?: string | null;
  /** Injected in tests so a download asserts without a real anchor click. */
  saver?: Saver;
  /** Injected in tests so the PDF viewer asserts without a real parser inside jsdom. */
  pdf?: PdfEngine;
  /**
   * The Planner's hand-off held for this tab (`planner-handoff.ts`), if any. Home shows its
   * banner and every Standards check gets its expected standards and context (KTD5).
   */
  planner?: PlannerSession | null;
}

export function CollabApp({
  auth, headerAction, analytics: injected, inviteToken = null, declineToken: heldDecline = null, saver, pdf,
  planner: heldPlanner = null,
}: CollabAppProps) {
  const { getToken, userId } = auth;
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<Analytics | null>(injected ?? null);

  const [stage, setStage] = useState<'loading' | 'denied' | 'ready'>('loading');
  const [deniedReason, setDeniedReason] = useState<string | undefined>();
  /**
   * This teacher's own address, as the Worker reported it.
   *
   * Kept because one screen needs it: `/new` refuses an invitation addressed to the person
   * creating the group, who is its admin from the instant it exists. It is the Worker's answer
   * rather than Clerk's, so the address the refusal compares against is the same one the
   * roster and the invitation match are written from (`normalizeEmail`).
   */
  const [myEmail, setMyEmail] = useState<string | null>(null);
  /**
   * A held decline wins over a held join. `invite-token.ts` already keeps the two tokens
   * under separate keys, so a `/decline` link never produces a join token; this is the
   * belt to that brace for a tab that somehow holds both — say, a join link opened and
   * abandoned earlier, then a decline link. The decline asks before it does anything, and an
   * automatic accept on the way to a decline is the one outcome this must never produce.
   */
  const [joining, setJoining] = useState(inviteToken !== null && heldDecline === null);
  const [declining, setDeclining] = useState(heldDecline !== null);
  /** As `token` below, and dropped once spent so a second `DeclinePage` holds nothing. */
  const [declineToken, setDeclineToken] = useState(heldDecline);
  /**
   * The token, as state rather than as the prop it arrived in.
   *
   * It is dropped the instant it is spent, and that is load-bearing now that `/join` is a
   * route: between `joining` turning false and the URL becoming the group's, there is a
   * render where the router still matches `/join` and mounts a *second* `JoinPage`. Its
   * single-post guard is per instance, so a second instance holding the same token posts the
   * same accept a second time — which the rate limiter counts, and which took the browser
   * suite over its ten-per-minute ceiling before the seventh scenario had run.
   */
  const [token, setToken] = useState(inviteToken);
  /** Dropped when the teacher dismisses the banner, which ends the hand-off for this tab. */
  const [planner, setPlanner] = useState(heldPlanner);
  const dismissPlanner = useCallback(() => {
    setPlanner((current) => { current?.dismiss(); return null; });
  }, []);

  /**
   * Clerk's `useAuth()` hands back a fresh `getToken` on every render. Closing over it
   * directly would give the API a new identity each render and re-run every effect that
   * depends on it, forever. The ref keeps the callback stable and the token current.
   */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const api: GetToken = useCallback(() => tokenRef.current(), []);

  const track = useCallback((event: AnalyticsEvent) => {
    try {
      analytics?.track(event);
    } catch (error) {
      // A throw from a handler would lose the upload or the comment it was attached to.
      console.warn('[analytics] track failed', error);
    }
  }, [analytics]);

  useEffect(() => {
    if (injected) return;
    let live = true;
    createAnalytics(userId ?? undefined, api)
      .then((a) => live && setAnalytics(a))
      .catch(() => live && setAnalytics(nullAnalytics()));
    return () => { live = false; stopAnalytics(); };
  }, [injected, userId, api]);

  useEffect(() => {
    let live = true;
    void (async () => {
      const me = await call<Me>(api, '/api/me');
      if (!live) return;
      if (!me.ok) {
        // 403 and 401 mean opposite things. 403 is "we know who you are and the tool is
        // switched off"; 401 is "we could not verify your session at all", which is almost
        // always configuration rather than permission.
        setDeniedReason(
          me.status === 403 ? me.reason : me.status === 401 ? 'session_invalid' : 'unreachable',
        );
        setStage('denied');
        return;
      }
      setMyEmail(me.data.email);
      setStage('ready');
    })();
    return () => { live = false; };
  }, [api]);

  /**
   * A hand-off naming a material opens it, once per hand-off (planner collab materials KTD6,
   * R8). After capture — or after the sign-in round trip, which returns to `/` — the teacher
   * lands on that material's page, where the Standards check reads the expected standards.
   * Not while a held invitation or decline is on screen: those answer first, and the open
   * waits for the next render that shows the routes. `replace`, so back does not return to a
   * home the teacher never saw.
   */
  useEffect(() => {
    const target = planner?.handoff.target;
    if (stage !== 'ready' || joining || declining || !target) return;
    if (!planner?.takeReport('open')) return;
    void navigate(materialPath(target.groupId, target.materialId), { replace: true });
  }, [stage, joining, declining, planner, navigate]);

  const viewed = useRef(false);
  useEffect(() => {
    if (!analytics || viewed.current) return;
    viewed.current = true;
    // The first arrival after a Planner hand-off says so, once per hand-off (KTD7).
    track(collabPageViewed(planner?.takeReport('arrival') ? ARRIVAL_SOURCE : undefined));
  }, [analytics, track, planner]);

  /*
   * Flags are read during render, so a render is what makes a late answer count. PostHog says
   * when its flags (re)load; bumping this re-reads both switches below.
   */
  const [, setFlagsSeen] = useState(0);
  useEffect(() => analytics?.onFlags(() => setFlagsSeen((n) => n + 1)), [analytics]);

  const switchedOff = analytics?.disabled() ?? false;
  // Fails open: no adapter yet, or one that cannot answer, draws the markers (R11).
  const showUnread = analytics?.unreadShown() ?? true;
  const shown = useRef(false);
  useEffect(() => {
    if (!switchedOff || shown.current) return;
    shown.current = true;
    track(collabKillSwitchShown());
  }, [switchedOff, track]);

  const header = <ShellHeader action={headerAction} onLabsClicked={(placement) => track(collabLabsClicked(placement))} />;

  if (stage === 'loading') {
    return <>{header}<main className="page"><p className="hint" role="status">Checking your access…</p></main></>;
  }

  if (stage === 'denied') {
    return (
      <>
        {header}
        <main className="page">
          <div className="panel notice" role="alert">
            <h2>Collab is not open to you right now.</h2>
            <p>{DENIAL_MESSAGES[deniedReason ?? ''] ?? DENIAL_MESSAGES.unreachable}</p>
          </div>
        </main>
      </>
    );
  }

  // The browser half of the kill switch. It only decides what this page renders; the Worker's
  // own read is the boundary, and both fail open (R20).
  if (switchedOff) {
    return (
      <>
        {header}
        <main className="page">
          <div className="panel notice" role="status">
            <h2>Collab is paused.</h2>
            <p>Your groups, materials and comments are all still here. Check back shortly.</p>
          </div>
        </main>
      </>
    );
  }

  /**
   * A held invitation wins over whatever route the teacher landed on.
   *
   * Clerk's hosted sign-in returns them to `/`, never to `/join` — the fragment their link
   * carried was stripped before the provider mounted, so `/join` would be an empty page
   * (`auth-config.ts`). The token is in `sessionStorage` instead, and the join screen renders
   * wherever they land. This must stay ahead of the routes for that reason.
   */
  const join = (
    <JoinPage
      token={token}
      getToken={api}
      onJoined={(group) => {
        setJoining(false);
        // `replace`, so the browser's back button does not return to a spent invitation.
        void navigate(groupPath(group.id), { replace: true });
      }}
      onSpent={() => { clearInviteToken(); setToken(null); }}
    />
  );

  if (joining) return <>{header}<main className="page">{join}</main></>;

  /**
   * A held decline, like a held join, renders wherever the teacher lands — Clerk's sign-in
   * returns them to `/`, not to `/decline`. Unlike a join it does not navigate on its own: it
   * waits on a confirm, and the result stays on screen until the teacher leaves for their
   * groups, which is when `declining` ends.
   */
  const decline = (
    <DeclinePage
      token={declineToken}
      getToken={api}
      onSpent={() => { clearDeclineToken(); setDeclineToken(null); }}
      onFinished={() => {
        // Cancel spends nothing on the Worker, but a link walked away from should not keep
        // offering itself in this tab.
        clearDeclineToken();
        setDeclineToken(null);
        setDeclining(false);
        void navigate(groupsPath(), { replace: true });
      }}
    />
  );

  if (declining) return <>{header}<main className="page">{decline}</main></>;

  return (
    <>
      {header}
      <main className="page">
        {/* Above the routes, so files keep uploading while the teacher moves about the group. */}
        <UploadsProvider getToken={api} track={track} {...(pdf ? { pdf } : {})}>
          <Routes>
            <Route
              path={ROUTE_GROUPS}
              element={<GroupsScreen api={api} showUnread={showUnread} planner={planner} onDismissPlanner={dismissPlanner} />}
            />
            {/* `main.tsx` has already read the fragment and pointed the bar at `/`; a typed
                address with nothing to read simply goes home. */}
            <Route path={ROUTE_FROM_PLANNER} element={<Navigate to={groupsPath()} replace />} />
            {/* A top-level literal. Every group's own address sits behind `/g/`, so no group a
                teacher can name can ever be reached at `/new`. */}
            <Route path={ROUTE_NEW_GROUP} element={<NewGroupScreen api={api} selfEmail={myEmail} />} />
            <Route
              path={ROUTE_GROUP}
              element={
                <GroupScreen
                  api={api}
                  viewerUserId={userId}
                  track={track}
                  showUnread={showUnread}
                  {...(saver ? { saver } : {})}
                  {...(pdf ? { pdf } : {})}
                />
              }
            />
            {/* A literal segment, so it can never be read as a material id. */}
            <Route
              path={ROUTE_NEW_MATERIAL}
              element={<NewMaterialScreen api={api} viewerUserId={userId} track={track} />}
            />
            <Route
              path={ROUTE_MATERIAL}
              element={
                <MaterialScreen
                  api={api}
                  viewerUserId={userId}
                  track={track}
                  planner={planner}
                  {...(saver ? { saver } : {})}
                  {...(pdf ? { pdf } : {})}
                />
              }
            />
            {/* Reached only by typing it: a real invitation arrives with a fragment and is held
                before any of this renders, so the branch above answers it. With nothing held,
                `JoinPage` says the one thing every refusal says (R10). */}
            <Route path={ROUTE_JOIN} element={join} />
            {/* The same, for a decline: a real link is held and answered by the branch above.
                Typed with nothing held, it refuses without asking the Worker anything. */}
            <Route path={ROUTE_DECLINE} element={decline} />
            <Route path="*" element={<NotFoundScreen />} />
          </Routes>
        </UploadsProvider>
      </main>
    </>
  );
}

/**
 * Everything under `/` — the teacher's groups, and the way to start one (R1).
 *
 * This screen no longer creates anything. Starting a group is `/new`, so what is left here is
 * the list and one primary control in the corner pointing at it.
 */
function GroupsScreen({ api, showUnread, planner, onDismissPlanner }: {
  api: GetToken; showUnread: boolean; planner: PlannerSession | null; onDismissPlanner: () => void;
}) {
  const navigate = useNavigate();
  const [invitationCount, setInvitationCount] = useState(0);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [groupsState, setGroupsState] = useState<FetchState>('loading');

  const loadGroups = useCallback(async () => {
    setGroupsState('loading');
    const result = await call<{ groups: GroupSummary[] }>(api, '/api/groups');
    if (!result.ok) { setGroupsState('failed'); return; }
    setGroups(result.data.groups);
    setGroupsState('ready');
  }, [api]);

  // On mount, which now includes coming back with the browser's own back button. The list a
  // teacher returns to is the list as it is, not as it was when they left it.
  useEffect(() => { void loadGroups(); }, [loadGroups]);

  return (
    <>
      {planner && <PlannerBanner handoff={planner.handoff} onDismiss={onDismissPlanner} />}
      <GroupList
        groups={groups}
        state={groupsState}
        linkTo={(group) => groupPath(group.id)}
        onRetry={() => { void loadGroups(); }}
        showUnread={showUnread}
        invitations={
          <PendingInvitations
            getToken={api}
            // Into the group just joined. The list behind the back button reloads on mount, so
            // it already counts the new group when the teacher returns to it.
            onJoined={(group) => { void navigate(groupPath(group.id)); }}
            onCountChange={setInvitationCount}
          />
        }
        hasInvitations={invitationCount > 0}
      />
    </>
  );
}

/**
 * `/new` — starting a group, and gathering the colleagues to invite, in one act.
 *
 * A real address, so it survives a cold load, a refresh and the back button. A success lands in
 * the new group's room and `replace`s this entry: the group is the thing the teacher wanted,
 * the room is where its invitation links are copied from, and the back button from there goes
 * to the groups list rather than back into a form that has already run.
 *
 * **What it lands with rides in the history entry's state, never in the URL.** The links carry
 * live invitation tokens; a token in a path or a query would reach Worker logs, referrers and
 * PostHog's `$current_url`, which is the leak `routes.ts` and `api.ts` are both written around.
 * `StartedGroupPanel`'s Done button replaces the entry with no state at all, so the links stop
 * riding along the moment the teacher is finished with them.
 */
function NewGroupScreen({ api, selfEmail }: { api: GetToken; selfEmail: string | null }) {
  const navigate = useNavigate();
  return (
    <NewGroupPage
      getToken={api}
      selfEmail={selfEmail}
      onCreated={(group, started) => {
        const carry = started.created.length > 0 || started.failed.length > 0 || started.members.length > 0;
        void navigate(groupPath(group.id), {
          replace: true,
          ...(carry ? { state: { started } } : {}),
        });
      }}
      onCancel={() => { void navigate(groupsPath()); }}
    />
  );
}

interface ScreenProps {
  api: GetToken;
  viewerUserId: string | null;
  track: (event: AnalyticsEvent) => void;
  saver?: Saver;
  pdf?: PdfEngine;
  /** The unread kill switch, read once in `CollabApp`. Absent on screens without markers. */
  showUnread?: boolean;
  /** The Planner's hand-off, for the material page's Standards check. */
  planner?: PlannerSession | null;
}

/** `/g/:groupId` — the room. The id comes from the URL and is never trusted, only asked. */
function GroupScreen({ api, viewerUserId, track, saver, pdf, showUnread = true }: ScreenProps) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * Only `/new` puts anything here, and only on the one navigation that follows a creation.
   * It is validated rather than cast: a history entry survives a refresh and anyone with a
   * console can write one, so a component that trusted its shape would render `undefined.map`.
   */
  const started: StartedGroup | null = asStartedGroup(location.state);
  if (!groupId) return <NotFoundScreen />;
  return (
    <GroupRoom
      key={groupId}
      groupId={groupId}
      getToken={api}
      viewerUserId={viewerUserId}
      track={track}
      onBack={() => { void navigate(groupsPath()); }}
      started={started}
      // Done means done: the entry is replaced with one carrying no state, so the links are
      // not sitting in session history behind the back button.
      onStartedDone={() => { void navigate(location.pathname, { replace: true, state: null }); }}
      showUnread={showUnread}
      {...(saver ? { saver } : {})}
      {...(pdf ? { pdf } : {})}
    />
  );
}

/**
 * `/g/:groupId/new` — adding a material.
 *
 * A real address, so it survives a cold load, a refresh and the back button. A success lands
 * on the new material's own page and `replace`s this entry: the teacher shared it to be read,
 * that page is where it is read and commented on, and the back button from there goes to the
 * room rather than back into a form whose file has already been sent.
 */
function NewMaterialScreen({ api, track }: ScreenProps) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  if (!groupId) return <NotFoundScreen />;
  return (
    <MaterialUploadPage
      key={groupId}
      groupId={groupId}
      getToken={api}
      track={track}
      onShared={(material) => {
        void navigate(materialPath(groupId, material.id), { replace: true });
      }}
      onCancel={() => { void navigate(groupPath(groupId)); }}
      onQueued={() => { void navigate(groupPath(groupId), { replace: true }); }}
    />
  );
}

/** `/g/:groupId/m/:materialId` — the material, and the conversation about it. */
function MaterialScreen({ api, viewerUserId, track, saver, pdf, planner = null }: ScreenProps) {
  const { groupId, materialId } = useParams();
  const navigate = useNavigate();
  if (!groupId || !materialId) return <NotFoundScreen />;
  return (
    <MaterialPage
      key={materialPath(groupId, materialId)}
      groupId={groupId}
      materialId={materialId}
      getToken={api}
      viewerUserId={viewerUserId}
      track={track}
      onBack={() => { void navigate(groupPath(groupId)); }}
      planner={planner}
      {...(saver ? { saver } : {})}
      {...(pdf ? { pdf } : {})}
    />
  );
}

/** Any other address. A way back, rather than a blank page. */
function NotFoundScreen() {
  return (
    <section className="panel" aria-label="Not found">
      <h2>That page is not here.</h2>
      <p>The address may be out of date, or it may have been mistyped.</p>
      <Link className="link" to={groupsPath()}>Back to your groups</Link>
    </section>
  );
}

function ClerkAuthed({ inviteToken, declineToken, planner }: {
  inviteToken: string | null; declineToken: string | null; planner: PlannerSession | null;
}) {
  const { getToken, userId } = useAuth();
  return (
    <CollabApp
      auth={{ getToken, userId: userId ?? null }}
      /*
       * The account control, on every route, because `ShellHeader` is rendered outside the
       * routes rather than by whichever screen happens to be on.
       *
       * `afterSignOutUrl` is deliberately absent: it is already set on the provider through
       * `clerkRedirects`, and a second copy here is a second place for it to be wrong.
       */
      headerAction={<UserButton />}
      inviteToken={inviteToken}
      declineToken={declineToken}
      planner={planner}
    />
  );
}

/**
 * The signed-out screen of a test-identity build, and the only thing in this file that has
 * no production counterpart.
 *
 * It renders only when `VITE_E2E_IDENTITY` compiled identities in, which is to say never in
 * anything shipped: a production bundle has an empty list and this component is unreachable.
 * Signing in writes the choice and reloads, so the invitee's return leg is a real page load
 * with no fragment on it — the trip `invite-token.ts`'s `sessionStorage` hold exists for, and
 * the one thing a signed-in-from-the-start test can never exercise.
 */
function TestSignIn({ identities, inviteToken }: { identities: readonly string[]; inviteToken: string | null }) {
  return (
    <>
      <ShellHeader />
      <main className="page centered">
        <div className="test-identity-banner" role="status">
          Test identity build — sign-in is bypassed. Not for production. Rebuild without{' '}
          <code>VITE_E2E_IDENTITY</code> to restore Clerk.
        </div>
        {inviteToken && (
          <p className="hint" role="status">
            Sign in to accept your invitation. We are holding it for you.
          </p>
        )}
        <section className="panel" aria-label="Sign in">
          <h2>Sign in</h2>
          <ul>
            {identities.map((entry) => (
              <li key={entry}>
                <button type="button" onClick={() => signInAsE2eIdentity(entry)}>
                  Sign in as {emailOfIdentity(entry)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </>
  );
}

/**
 * The top of the tree.
 *
 * `inviteToken` is read from the holding store rather than from the URL: by the time any of
 * this renders, `main.tsx` has already stripped the fragment (R9). An invitee who had to
 * sign in returns here through a redirect with no fragment at all, and the held token is the
 * only thing that survived that trip.
 */
export default function App() {
  const inviteToken = heldInviteToken();
  // Held under its own key, so it can only ever reach `DeclinePage` (KTD7).
  const declineToken = heldDeclineToken();
  // The Planner's hand-off, held across the sign-in round trip like the tokens above (KTD4).
  const [planner] = useState(() => plannerSession(browserHandoffStorage()));
  // A local const, so the narrowing below survives into the closure that reads it. An
  // imported binding is not narrowed inside a callback, and the resulting `string |
  // undefined` is not a token.
  // Short-circuited rather than folded into `selectE2eIdentity`, so a production bundle —
  // where the list is empty — never touches `sessionStorage` at all.
  const identity = e2eIdentities.length > 0
    ? selectE2eIdentity(e2eIdentities, chosenE2eIdentity())
    : null;

  // A test-identity build with no identity chosen yet. Three identities are compiled in for
  // U10 and a tab is signed in as exactly one of them, so "none chosen" is a real state and
  // it is the signed-out one — the same screen shape `SignedOut` renders below, including
  // the held-invitation line, because the invitee who has to sign in first is the whole
  // reason this branch exists.
  if (e2eIdentities.length > 0 && identity === null) {
    return <TestSignIn identities={e2eIdentities} inviteToken={inviteToken} />;
  }

  if (identity) {
    return (
      <>
        {/*
          * Loud on purpose. This branch wins over a compiled-in publishable key, which is
          * what the browser suite needs — but a stray VITE_E2E_IDENTITY at build time makes
          * a bundle that silently stops using Clerk and sends a bare string where a JWT
          * belongs. The Worker refuses it correctly and the error points nowhere near the
          * cause, so the banner turns ten minutes of confusion into a glance.
          */}
        <div className="test-identity-banner" role="status">
          Test identity build — sign-in is bypassed as{' '}
          <code>{identity.split(':')[0]}</code>. Not for production. Rebuild without{' '}
          <code>VITE_E2E_IDENTITY</code> to restore Clerk.
        </div>
        <CollabApp
          auth={{ getToken: async () => identity, userId: identity.split(':')[0] ?? null }}
          /*
           * The test-identity build's account control. There is no Clerk in this bundle to
           * draw an avatar, and an empty corner would make the header look broken on every
           * screen of the browser suite — so it says who this tab is, which is the one thing
           * `UserButton` is there to say.
           */
          headerAction={<span className="shell-identity meta">{emailOfIdentity(identity)}</span>}
          inviteToken={inviteToken}
          declineToken={declineToken}
          planner={planner}
        />
      </>
    );
  }

  // No key means no sign-in control, and this tool has no anonymous path. Say so plainly
  // rather than rendering a page whose only control silently does nothing.
  if (!publishableKey) {
    return (
      <>
        <ShellHeader />
        <main className="page">
          <div className="panel notice" role="status">
            <h2>Sign-in is not configured.</h2>
            <p>
              VITE_CLERK_PUBLISHABLE_KEY was not set when this bundle was built. See
              packages/collab/README.md.
            </p>
          </div>
        </main>
      </>
    );
  }

  return (
    <ClerkProvider publishableKey={publishableKey} {...clerkRedirects}>
      <SignedOut>
        {/*
          * Clerk's hosted sign-in, not the embedded component.
          *
          * `RedirectToSignIn` leaves this origin for the instance's Account Portal and
          * comes back to `/` (see `clerkRedirects`). That round trip is the reason
          * `invite-token.ts` holds the token in `sessionStorage` rather than in memory:
          * the fragment does not survive a hosted redirect, so an invitee arriving signed
          * out would otherwise lose their invitation on the way to sign in.
          */}
        <>
          <ShellHeader />
          <main className="page centered">
            {inviteToken && (
              <p className="hint" role="status">
                Sending you to sign in. We are holding your invitation.
              </p>
            )}
            <RedirectToSignIn />
          </main>
        </>
      </SignedOut>
      <SignedIn>
        <ClerkAuthed inviteToken={inviteToken} declineToken={declineToken} planner={planner} />
      </SignedIn>
    </ClerkProvider>
  );
}
